import BackendConfig from "src/backendConfig";

async function parseJsonSafe(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return { raw: text };
  }
}

function findMissingInPayload(payload, depth = 0) {
  if (!payload || depth > 6) return [];

  if (Array.isArray(payload)) {
    const direct = payload
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (direct.length > 0) return direct;

    for (const item of payload) {
      const nested = findMissingInPayload(item, depth + 1);
      if (nested.length > 0) return nested;
    }
    return [];
  }

  if (typeof payload !== 'object') return [];

  const candidateKeys = ['missing', 'Missing', 'missingFields', 'MissingFields', 'required', 'Required'];
  for (const key of candidateKeys) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
    const value = payload[key];
    if (Array.isArray(value)) {
      const normalized = value
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      if (normalized.length > 0) return normalized;
    }
  }

  for (const value of Object.values(payload)) {
    const nested = findMissingInPayload(value, depth + 1);
    if (nested.length > 0) return nested;
  }

  return [];
}

function normalizeErrorData(json) {
  const data = (json && typeof json === 'object') ? { ...json } : { raw: json };
  const missing = findMissingInPayload(json);
  if (missing.length > 0) {
    data.missing = missing;
  }
  return data;
}

function getErrorMessage(json, status) {
  if (!json || typeof json !== 'object') {
    return `HTTP ${status}`;
  }

  const direct = [json.message, json.error, json.raw].find((v) => String(v || '').trim());
  if (direct) return direct;

  const nestedCandidates = [
    json?.data?.message,
    json?.data?.error,
    json?.error?.message,
    json?.error?.error,
    json?.details?.message,
    json?.details?.error,
  ].find((v) => String(v || '').trim());

  return nestedCandidates || `HTTP ${status}`;
}

function resolveBridgeStripeUrl(path) {
  const rawBase = String(BackendConfig.httpBackend || "").trim();
  if (!rawBase) return path;

  const baseWithScheme = /^https?:\/\//i.test(rawBase)
    ? rawBase
    : `https://${rawBase}`;

  const base = baseWithScheme.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

async function request(path, { method = "GET", body } = {}) {
  const res = await fetch(resolveBridgeStripeUrl(path), {
    method,
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  const json = await parseJsonSafe(res);

  // Special-case: "No customer" / not onboarded is expected.
  // Backend may return either a raw string or an object like:
  // { message: "No customer associated with logged in Legal id", code: "no_customer" }
  const code = String(json?.code || "").toLowerCase();
  const message = String(json?.message || json?.error || json?.raw || "").toLowerCase();
  const isNoCustomer = code === "no_customer";

  if (!res.ok && res.status == 404) {
    return { ok: true, notFound: true, data: null };
  }

  if (!res.ok) {
    const normalizedData = normalizeErrorData(json);
    return {
      ok: false,
      status: res.status,
      error: getErrorMessage(json, res.status),
      data: normalizedData,
    };
  }

  return { ok: true, data: json };
}

export const bridgeStripeApi = {
  // Customer
  getCustomer: () => request("/BridgeStripe/Customer"),
  deleteCustomer: () => request("/BridgeStripe/Customer", { method: "DELETE" }),

  createCustomer: ({ email } = {}) => {
    const normalizedEmail = String(email || '').trim();

    return request("/BridgeStripe/Customer", {
      method: "POST",
      body: normalizedEmail ? { email: normalizedEmail } : {},
    });
  },

  // KYC + ToS
  getKyc: ({ isPix = false } = {}) => {
    const params = new URLSearchParams();
    params.set("endorsement", isPix ? "pix" : "base");
    return request(`/BridgeStripe/Kyc?${params.toString()}`);
  },

  // Exchange rate
  getExchangeRate: ({ amount, from, to } = {}) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
      throw new Error("Invalid amount");
    }
    const currency = String(from || to || "").trim();
    if (!currency) {
      throw new Error("Missing currency");
    }

    const params = new URLSearchParams();
    params.set("amount", String(numericAmount));
    params.set("from", String(from));
    params.set("to", String(to));

    return request(`/BridgeStripe/Exchange?${params.toString()}`);
  },

  getTransfer: (opts = {}) => {
    const method = String(opts?.method || "").trim().toLowerCase();
    if (method === "pix") {
      return request("/NeuroExchange/CreatePayment");
    }
    return request("/NeuroExchange/CreatePayment");
  },
};
